import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import GrindrodLogo from "../components/GrindrodLogo";

// Department-level role options shown to the registrant.
// `role` maps to the underlying auth role (admin / manager / supervisor).
// `label` is stored on `position` so it's visible on the members list,
// approvals screen, and anywhere the user's title is displayed.
const ROLE_OPTIONS = [
    { label: "Plant Management",        role: "manager",        group: "Management" },
    { label: "GTRB Management",         role: "manager",        group: "Management" },
    { label: "RBTG Management",         role: "manager",        group: "Management" },
    { label: "Electrical Line Manager", role: "manager",        group: "Management" },
    { label: "Maintenance Planner",     role: "manager",        group: "Management" },
    { label: "GTRB Supervisor",         role: "supervisor",     group: "Operations" },
    { label: "Weighbridge Supervisor",  role: "weighbridge_supervisor", group: "Operations" },
    { label: "RBTG Supervisors",        role: "supervisor",     group: "Operations" },
    { label: "Kusasa Control Room",     role: "supervisor",     group: "Operations" },
    { label: "Safety Department",       role: "safety",         group: "Support Departments" },
    { label: "Client Liaison",          role: "client_liaison", group: "Support Departments" },
    { label: "Finance Department",      role: "finance",        group: "Support Departments" },
    { label: "Fleet Manager",           role: "fleet_manager",  group: "Support Departments" },
    { label: "Plant Manager (view-only)", role: "plant_manager", group: "Management" },
    { label: "FEL Operator",            role: "fel_operator",   group: "Operations" },
    { label: "Tippler Operator",        role: "tippler_operator", group: "Operations" },
    { label: "Shift Millwright",        role: "shift_millwright", group: "Operations" },
    { label: "Electrician",              role: "electrician", group: "Operations" },
    { label: "HR / People",               role: "hr",             group: "Support Departments" },
    { label: "Grindrod Rail Coordinator", role: "rail_coordinator",   group: "Rail Operations" },
    { label: "TFR Yard Controller",       role: "tfr_yard",           group: "Rail Operations" },
    { label: "Mine Rail Planner",         role: "mine_rail_planner",  group: "Rail Operations" },
];

// Pre-compute grouped lists once at module scope so render-time filter() calls
// (one per <optgroup>) don't re-iterate ROLE_OPTIONS on every keystroke.
const ROLE_BY_GROUP = {
    "Management":          ROLE_OPTIONS.filter((o) => o.group === "Management"),
    "Operations":          ROLE_OPTIONS.filter((o) => o.group === "Operations"),
    "Support Departments": ROLE_OPTIONS.filter((o) => o.group === "Support Departments"),
    "Rail Operations":     ROLE_OPTIONS.filter((o) => o.group === "Rail Operations"),
};

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "",
        position: "",
        username: "",
        email: "",
        password: "",
        role: "supervisor",
        terminal: "richards_bay",
        mine_name: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const selectRole = (label) => {
        const opt = ROLE_OPTIONS.find((o) => o.label === label);
        if (opt) setForm((f) => ({ ...f, position: opt.label, role: opt.role }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.position) {
            toast.error("Please select a role");
            return;
        }
        if (form.role === "mine_rail_planner" && !form.mine_name.trim()) {
            toast.error("Mine name is required for the Mine Rail Planner role");
            return;
        }
        setLoading(true);
        try {
            await register(form);
            setSubmitted(true);
            toast.success("Registration submitted. Await approval.");
        } catch (err) {
            toast.error(err.message || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--surface-alt)] px-6">
                <div className="g-card max-w-md w-full p-8 text-center" data-testid="register-pending">
                    <div className="g-pill g-pill-amber mx-auto mb-4">Pending approval</div>
                    <h2 className="font-display font-black text-2xl text-[var(--grindrod-navy)] mb-2">
                        Thanks, {form.name.split(" ")[0]}.
                    </h2>
                    <p className="text-slate-600 text-sm">
                        Your registration has been sent to the managers for approval. You'll be able to sign in once it's
                        accepted.
                    </p>
                    <button
                        onClick={() => nav("/login")}
                        className="btn-navy mt-6"
                        data-testid="register-back-to-login"
                    >
                        Back to sign in
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            <div className="flex flex-col justify-center px-6 md:px-16 py-14 bg-white">
                <div className="max-w-md w-full">
                    <div className="mb-10">
                        <GrindrodLogo size="xl" subtitle="GRINDROD TERMINALS" />
                    </div>

                    <h1 className="font-display font-black text-3xl md:text-4xl text-[var(--grindrod-navy)] mb-2">
                        Request access
                    </h1>
                    <p className="text-slate-500 text-sm mb-6">
                        Managers approve all new accounts.
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                        <div>
                            <label className="uppercase-label block mb-1.5">Terminal <span className="text-[var(--grindrod-red)]">*</span></label>
                            <select
                                value={form.terminal}
                                onChange={set("terminal")}
                                className="g-input"
                                required
                                data-testid="register-terminal-input"
                            >
                                <option value="richards_bay">Grindrod Richards Bay Terminal</option>
                                <option value="maputo">Grindrod Maputo Terminal</option>
                            </select>
                            {form.terminal !== "richards_bay" && (
                                <div className="text-[11px] text-[var(--grindrod-red)] font-semibold uppercase-label mt-1">
                                    Coming soon · you'll get early access when this terminal launches
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Full Name</label>
                            <input value={form.name} onChange={set("name")} className="g-input" required data-testid="register-name-input" />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Role / Department <span className="text-[var(--grindrod-red)]">*</span></label>
                            <select
                                value={form.position}
                                onChange={(e) => selectRole(e.target.value)}
                                className="g-input"
                                required
                                data-testid="register-position-input"
                            >
                                <option value="">Select your role…</option>
                                <optgroup label="Management">
                                    {ROLE_BY_GROUP["Management"].map((o) => (
                                        <option key={o.label} value={o.label}>{o.label}</option>
                                    ))}
                                </optgroup>
                                <optgroup label="Operations">
                                    {ROLE_BY_GROUP["Operations"].map((o) => (
                                        <option key={o.label} value={o.label}>{o.label}</option>
                                    ))}
                                </optgroup>
                                <optgroup label="Support Departments">
                                    {ROLE_BY_GROUP["Support Departments"].map((o) => (
                                        <option key={o.label} value={o.label}>{o.label}</option>
                                    ))}
                                </optgroup>
                                <optgroup label="Rail Operations">
                                    {ROLE_BY_GROUP["Rail Operations"].map((o) => (
                                        <option key={o.label} value={o.label}>{o.label}</option>
                                    ))}
                                </optgroup>
                            </select>
                            {form.position && (
                                <div className="text-[11px] text-slate-500 mt-1">
                                    Access level: <span className="font-semibold text-[var(--grindrod-navy)] capitalize">{form.role.replace("_", " ")}</span>{" "}
                                    {form.role === "supervisor" ? "— can capture vessel data"
                                     : form.role === "manager" ? "— view-only + approvals/remarks"
                                     : form.role === "safety" ? "— logs safety incidents and LTIFR data"
                                     : form.role === "client_liaison" ? "— logs client complaints and feedback"
                                     : form.role === "finance" ? "— inputs budget, overtime and labour costs"
                                     : form.role === "fel_operator" ? "— mobile operator dashboard with live GPS tracking"
                                     : form.role === "fleet_manager" ? "— manages the FEL fleet: machines, service hours, out-of-service flags, live view"
                                     : form.role === "plant_manager" ? "— view-only access to all department dashboards (cannot input data)"
                                     : form.role === "shift_millwright" ? "— receives Grindrod breakdown alerts, accepts call-outs, adjusts ETA, and posts repair feedback"
                                     : form.role === "tippler_operator" ? "— captures coal tippler wagon offloading times and delay reasons"
                                     : form.role === "hr" ? "— HR analytics dashboard with anonymous engagement survey insights (Six-Sigma)"
                                     : form.role === "rail_coordinator" ? "— captures Grindrod rail arrivals, offload progress and completion warnings to TFR"
                                     : form.role === "tfr_yard" ? "— captures Transnet yard arrivals, releases and empty-wagon handovers"
                                     : form.role === "mine_rail_planner" ? "— creates consignments and captures mine-side loading + dispatch times"
                                     : ""}
                                </div>
                            )}
                            {form.role === "mine_rail_planner" && (
                                <div className="mt-3">
                                    <label className="uppercase-label block mb-1.5">Mine name <span className="text-red-500">*</span></label>
                                    <input
                                        value={form.mine_name}
                                        onChange={set("mine_name")}
                                        className="g-input"
                                        placeholder="e.g. Alpha Coal Mine"
                                        required
                                        data-testid="register-mine-name"
                                    />
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        Your account will be pinned to this mine. You will only see consignments for your own mine — other mines' data stays private.
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Username</label>
                            <input
                                value={form.username}
                                onChange={set("username")}
                                className="g-input"
                                placeholder="Pick a unique username"
                                required
                                minLength={3}
                                data-testid="register-username-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">
                                Email <span className="text-slate-400 lowercase font-normal">(optional)</span>
                            </label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                className="g-input"
                                placeholder="you@company.com"
                                data-testid="register-email-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Password</label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={set("password")}
                                className="g-input"
                                required
                                minLength={6}
                                data-testid="register-password-input"
                            />
                            <div className="text-xs text-slate-500 mt-1">Minimum 6 characters.</div>
                        </div>
                        <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-button">
                            {loading ? "Submitting…" : "Submit for approval"}
                        </button>
                    </form>

                    <div className="mt-8 text-sm text-slate-600">
                        Already approved?{" "}
                        <Link to="/login" className="text-[var(--grindrod-red)] font-semibold" data-testid="go-login-link">
                            Sign in
                        </Link>
                    </div>
                </div>
            </div>

            <div className="hidden md:flex login-bg items-end p-12">
                <div className="text-white max-w-md">
                    <div className="uppercase-label text-slate-300 mb-3">Onboarding</div>
                    <div className="font-display font-black text-4xl leading-tight">
                        One terminal. One source of truth.
                    </div>
                    <p className="mt-4 text-slate-200 text-sm">
                        Your account unlocks the live tracker after a manager verifies your credentials.
                    </p>
                </div>
            </div>
        </div>
    );
}
